import { useQueries } from "@tanstack/react-query";
import React, { createContext, PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import { InventoryEntry } from "../lib/model";
import { inventoryQueryKeys } from "../modules/inventory/inventory.queryKeys";
import { useInventoryService } from "../modules/inventory/inventory.service";

interface IPropertyInventories {
  propertyTicker: string;
  inventoryTickers: InventoryEntry[] | undefined | null;
}

type InventoryData = IPropertyInventories[] | undefined | null;

const Context = createContext<InventoryData>(undefined);

interface PropertyInventoriesProviderProps {
  propertyTickers: string[];
}

export function PropertyInventoriesProvider({
  propertyTickers,
  children,
}: PropsWithChildren<PropertyInventoriesProviderProps>) {
  const inventoryService = useInventoryService();
  const locale = useIntl().locale;

  const { ready, entries, errors } = useQueries({
    queries: propertyTickers.map(propertyTicker => ({
      queryKey: inventoryQueryKeys.list(propertyTicker, locale),
      queryFn: () => inventoryService.getInventoryEntries(propertyTicker, locale),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    })),
    combine: results => ({
      ready: results.every(result => result.isSuccess),
      entries: results.map(result => result.data),
      errors: results.map(result => result.error).filter((error): error is Error => error != null),
    }),
  });

  useEffect(() => {
    errors.forEach(error => console.error("Error retrieving inventory lines", error));
  }, [errors]);

  const data = useMemo<InventoryData>(
    () =>
      ready
        ? propertyTickers.map((propertyTicker, index) => ({
            propertyTicker,
            inventoryTickers: entries[index],
          }))
        : [],
    [ready, entries, propertyTickers]
  );

  return <Context.Provider value={data}>{children}</Context.Provider>;
}

export function usePropertyInventoryData(): InventoryData {
  return useContext(Context);
}
